import {of as observableOf, Observable} from 'rxjs';

import {map, takeWhile, catchError} from 'rxjs/operators';
import {DetailControl, ValuesObject} from '../detail-tabconfig';
import {ChangeDetectorRef, Input, OnDestroy, Directive} from '@angular/core';
import {ControlNameNotFound} from '../../../error/errordefinitions';
import {ErrorService} from '../../../error/error.service';
import {UIError} from '../../../error/uierror';
import {ControlParentInterface} from '../control-parent.interface';

@Directive()
export abstract class AbstractControlReadonlyComponent implements OnDestroy {
  private _freshResource: boolean;

  @Input()
  set changeDetection(changeDetection: boolean) {
    if (changeDetection) {
      this.changeDetector.reattach();
    } else {
      this.changeDetector.detach();
    }
  }

  @Input()
  set freshResource(freshResource: boolean) {
    this._freshResource = freshResource;
    this.freshResourceChanged();
  }

  get freshResource(): boolean {
    return this._freshResource;
  }

  protected freshResourceChanged() {}

  _config: DetailControl;

  constructor(
    public _parent: ControlParentInterface,
    protected sourceAlternateName: string = null,
    public changeDetector: ChangeDetectorRef,
    private errorService: ErrorService,
  ) {
    this.changeDetector.detach();
  }

  protected alive: boolean = true;

  ngOnDestroy(): void {
    this.alive = false;
  }

  public get isEnabled(): Observable<boolean> {
    if (this._config != null) {
      return this._config.Enabled.pipe(
        catchError((error, caught) => this.handleError(error, caught)),
      );
    }
    return observableOf(null);
  }

  @Input()
  set config(conf: DetailControl) {
    if (this._config != conf) {
      this._config = conf;

      if (!this._config.hasProperties()) {
        console.error('No Properties found in configuration!');
        console.debug(conf);
        return;
      }

      this.initSource();
      this.initMain();
    }
  }

  protected sourceVoObs: Observable<ValuesObject<any>> = null;
  protected sourceObs: Observable<any> = null;

  protected initSource(): void {
    if (this._config == null) {
      throw Error('Initialized target too early.');
    }

    let sourcePropertyName: string = null;

    if (this._config.propertyExists('Source')) {
      sourcePropertyName = 'Source';
    } else if (
      this.sourceAlternateName != null &&
      this._config.propertyExists(this.sourceAlternateName)
    ) {
      sourcePropertyName = this.sourceAlternateName;
    }

    if (sourcePropertyName != null) {
      this.sourceVoObs = this._config.getProperty(sourcePropertyName).pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
      );
      this.sourceObs = this._config.getPropertyValues(sourcePropertyName).pipe(
        map(val => {
          if (Array.isArray(val)) {
            // prevent change of base resource
            return val.slice();
          } else {
            return val;
          }
        }),
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
      );
    }
  }

  protected abstract initMain(): void;

  getCaughtObservable(obs: Observable<any>): Observable<any> {
    if (obs == null) return null;
    return obs.pipe(catchError((error, caught) => this.handleError(error, caught)));
  }

  handleError(error: any, caught?: Observable<any>) {
    if (this._config != null) {
      this._config.hasErrors = true;
    }
    if (error.name != ControlNameNotFound.name && !(typeof error === 'object' && status in error)) {
      this._config.Name.pipe(
        catchError((error, caught) => this.handleError(new ControlNameNotFound(), caught)),
      ).subscribe(name => {
        if (this._parent != null) {
          if (name != null && name.Values != null) {
            this._parent.addControlError(name.Values, String(error.message));
          } else {
            this._parent.addControlError('Unknown Control', String(error.message));
          }
        }
      });
    } else {
      if (error.name == ControlNameNotFound.name) {
        if (this._parent != null) {
          this._parent.addControlError('Unknown Control', String(error.message));
        }
      }
      if (status in error) {
        switch (error.status) {
          default:
            break;
          case 503:
            break; //Service Not Available
          case 400:
            break; //Page Not Found
        }
      }
    }
    console.error(error);
    try {
      let code = error.name;
      if (status in error) {
        code = error.status;
      }
      this.errorService.reportError(new UIError(error.name, code, error.message, 'Detail/Wizard'));
    } catch (error) {
      console.log(error.message);
    }
    return observableOf(null);
  }
}
